import React, { useEffect, useRef } from 'react';
import { WidgetButton } from '../WidgetButton';
import { FloatingMessage } from '../FloatingMessage';
import { ToggleView } from '../ToggleView';
import { OfflineMessage } from '../OfflineMessage';
import { VIEW } from '@/types';
import { getHasCompletedForm } from '@/utils/form-state';
import {
  useInterfaceStore,
  useConfigStore,
  useChatStore,
  useContextStore,
} from '@/hooks/stores';
import { Home } from '@/components/Home';
import { Header } from '../Header';
import './styles.scss';
import clsx from 'clsx';
import Components from './components';
import { Chat } from '@/components/Chat';
import { Questionnaire } from '@/components/Questionnaire';
import { Text } from '../Text';
import { resolveStringOrPageContextFunction } from '@/utils';

interface Props {
  serviceAvailable: boolean;
  onSendMessage: (content: string) => void;
  onTyping: (typing: boolean) => void;
  onFormSubmit: (formData: Record<string, string>) => void;
  onFormInteraction?: () => void;
  onContinueToChat: () => void;
  onActivity?: () => void;
  onMessageSeen?: (frontMessageId: string) => void;
}

export const Widget: React.FC<Props> = ({
  serviceAvailable,
  onSendMessage,
  onTyping,
  onFormSubmit,
  onFormInteraction,
  onContinueToChat,
  onActivity,
  onMessageSeen,
}) => {
  const { pageContext, customContext } = useContextStore();
  const {
    isFormSubmitting,
    sessionId,
    filledQuestionnaireData,
    resetUnreadCount,
  } = useChatStore();
  const { widgetIsOpen, widgetActiveView, isInlineMode } = useInterfaceStore();

  // Derive hasCompletedForm from existing state
  const hasCompletedForm = getHasCompletedForm(
    sessionId,
    filledQuestionnaireData,
    customContext.proposalId as string | undefined
  );
  const config = useConfigStore();
  const {
    ui: { formOnly, position, showBubble },
    features: {
      chat: { confirmMessage },
    },
  } = config;

  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const hasRenderedAtleastOnceRef = useRef<boolean>(false);

  const isOffline = !serviceAvailable;
  const isHomeTabEnabled = config.features?.enableHomeTab ?? false;

  // Reset unread count when widget opens
  useEffect(() => {
    if (widgetIsOpen) {
      resetUnreadCount();
    }
  }, [widgetIsOpen, resetUnreadCount]);

  // In inline mode, widget should always be open and in CHAT view
  // If home tab is disabled, always use CHAT view
  const effectiveWidgetIsOpen = isInlineMode ? true : widgetIsOpen;
  const effectiveActiveView =
    isInlineMode || !isHomeTabEnabled ? VIEW.CHAT : widgetActiveView;

  // Set up Intersection Observer to detect when widget becomes visible
  useEffect(() => {
    if (!widgetContainerRef.current || !onActivity) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // When widget becomes visible (e.g., user switches back to tab or scrolls to widget)
          if (entry.isIntersecting && effectiveWidgetIsOpen) {
            onActivity();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when at least 10% of widget is visible
      }
    );

    observer.observe(widgetContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onActivity, effectiveWidgetIsOpen]);

  // Prevent scroll events from propagating to the main page
  useEffect(() => {
    const container = widgetContainerRef.current;
    if (!container) return;

    const handleScroll = (e: Event) => {
      e.stopPropagation();
    };

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.stopPropagation();
    };

    // Add event listeners to prevent scroll propagation
    container.addEventListener('scroll', handleScroll, { passive: false });
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // Apply inline styles to chat window when in inline/target mode
  useEffect(() => {
    if (!isInlineMode) return;

    // widgetContainerRef.current IS the chat window element (attached via containerRef)
    const chatWindow = widgetContainerRef.current;

    if (chatWindow) {
      // Apply inline styles to override CSS and ensure inline rendering
      chatWindow.style.setProperty('position', 'static', 'important');
      chatWindow.style.setProperty('bottom', 'auto', 'important');
      chatWindow.style.setProperty('right', 'auto', 'important');
      chatWindow.style.setProperty('top', 'auto', 'important');
      chatWindow.style.setProperty('left', 'auto', 'important');
      chatWindow.style.setProperty('width', '100%', 'important');
      chatWindow.style.setProperty('min-height', '100%', 'important');
      chatWindow.style.setProperty('height', 'auto', 'important');
      chatWindow.style.setProperty('max-width', '100%', 'important');
      chatWindow.style.setProperty('max-height', 'none', 'important');
      chatWindow.style.setProperty('transform', 'none', 'important');
      chatWindow.style.setProperty('margin', '0', 'important');
    }
  }, [isInlineMode, effectiveWidgetIsOpen, effectiveActiveView]);

  // Show success message only after successful submission
  const confirmMessageText = resolveStringOrPageContextFunction({
    value: confirmMessage,
    pageContext,
    customContext,
  });

  return (
    <>
      {/* Only show floating elements when NOT in inline mode and showBubble is true */}
      {!isInlineMode && showBubble !== false && <WidgetButton />}
      {!isInlineMode && showBubble !== false && <FloatingMessage />}
      <Components.Container
        containerRef={widgetContainerRef}
        position={position}
        className={clsx(
          formOnly ? 'peptalk-inline-form-wrapper' : 'peptalk-chat-window',
          (effectiveWidgetIsOpen || formOnly) && 'open',
          (effectiveActiveView === VIEW.CHAT || formOnly) && 'chat-view',
          formOnly && 'overflow-y-auto'
        )}
      >
        {(effectiveWidgetIsOpen || hasRenderedAtleastOnceRef.current) &&
          (() => {
            hasRenderedAtleastOnceRef.current = true;

            return (
              <>
                {!formOnly && <Header />}

                {(() => {
                  if (isOffline) {
                    return <OfflineMessage />;
                  }

                  // Form only mode
                  if (formOnly) {
                    return (
                      <>
                        <Questionnaire
                          disabled={hasCompletedForm}
                          onSubmitQuestionnaire={onFormSubmit}
                          onFirstInteraction={onFormInteraction}
                        />
                        {hasCompletedForm && !isFormSubmitting && (
                          <div className="place-items-center grid mb-lg py-lg">
                            <Text size="lg" center>
                              {confirmMessageText}
                            </Text>
                          </div>
                        )}
                      </>
                    );
                  }

                  // Regular chat experience mode
                  return (
                    <>
                      {effectiveActiveView === VIEW.HOME &&
                        isHomeTabEnabled && <Home />}
                      {effectiveActiveView === VIEW.CHAT && (
                        <Chat
                          onSendMessage={onSendMessage}
                          onTyping={onTyping}
                          onFormSubmit={onFormSubmit}
                          onFormInteraction={onFormInteraction}
                          onContinueToChat={onContinueToChat}
                          onActivity={onActivity}
                          onMessageSeen={onMessageSeen}
                        />
                      )}
                      {/* Only show toggle view when NOT in inline mode AND home tab is enabled */}
                      {!isInlineMode && isHomeTabEnabled && <ToggleView />}
                    </>
                  );
                })()}
              </>
            );
          })()}
      </Components.Container>
    </>
  );
};
